import { useEffect } from 'react';
import { REGIONS } from '../data/regions';
import { ROUTES } from '../data/routes';
import { LANDMARKS } from '../data/landmarks';
import { REGION_LINKS, LANDMARK_LINKS, ROUTE_LINKS, APP_LABELS, urlFor } from '../data/crossLinks';
import type { MapEntity } from '../data/types';
import styles from './DetailPanel.module.css';

interface DetailPanelProps {
  selected: MapEntity;
  onClose: () => void;
  onJumpToLandmark: (id: string) => void;
}

export function DetailPanel({ selected, onClose, onJumpToLandmark }: DetailPanelProps) {
  // Escape closes the drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop closes the drawer on click but stays inside the
       *  map stage so the topbar / footer remain reachable. */}
      <button
        type="button"
        className={styles.backdrop}
        onClick={onClose}
        aria-label="close panel"
      />
      <aside
        className={styles.panel}
        role="dialog"
        aria-modal="false"
        aria-label="detail"
      >
        <button
          type="button"
          className={styles.closeX}
          onClick={onClose}
          aria-label="close"
        >
          ×
        </button>
        <DetailBody selected={selected} onJumpToLandmark={onJumpToLandmark} />
      </aside>
    </>
  );
}

function DetailBody({
  selected,
  onJumpToLandmark,
}: {
  selected: MapEntity;
  onJumpToLandmark: (id: string) => void;
}) {
  if (selected.type === 'region') {
    const region = REGIONS.find((r) => r.id === selected.id);
    if (!region) return null;
    const landmarks = region.landmarks
      .map((id) => LANDMARKS.find((l) => l.id === id))
      .filter((l): l is NonNullable<typeof l> => Boolean(l));
    const links = REGION_LINKS[region.id] ?? [];
    return (
      <>
        <header className={styles.header}>
          <span className={styles.kind} style={{ color: region.accent }}>
            region
          </span>
          <h2 className={styles.title}>{region.name}</h2>
          <p className={styles.brief}>{region.brief}</p>
        </header>
        <p className={styles.body}>{region.long}</p>
        {landmarks.length > 0 && (
          <>
            <h3 className={styles.sub}>landmarks in this region</h3>
            <ul className={styles.lmList}>
              {landmarks.map((lm) => (
                <li key={lm.id}>
                  <button
                    type="button"
                    className={styles.lmBtn}
                    onClick={() => onJumpToLandmark(lm.id)}
                  >
                    <span className={styles.lmName}>{lm.name}</span>
                    <span className={styles.lmBrief}>{lm.brief}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        <CrossLinks links={links} />
      </>
    );
  }

  if (selected.type === 'route') {
    const route = ROUTES.find((r) => r.id === selected.id);
    if (!route) return null;
    const links = ROUTE_LINKS[route.id] ?? [];
    return (
      <>
        <header className={styles.header}>
          <span className={styles.kind}>route · {route.doseLevel}</span>
          <h2 className={styles.title}>{route.name}</h2>
        </header>
        <p className={styles.body}>{route.description}</p>
        <CrossLinks links={links} />
      </>
    );
  }

  // landmark
  const lm = LANDMARKS.find((l) => l.id === selected.id);
  if (!lm) return null;
  const parent = lm.region ? REGIONS.find((r) => r.id === lm.region) : null;
  const links = LANDMARK_LINKS[lm.id] ?? [];
  return (
    <>
      <header className={styles.header}>
        <span className={styles.kind}>landmark · {lm.kind}</span>
        <h2 className={styles.title}>{lm.name}</h2>
        <p className={styles.brief}>{lm.brief}</p>
      </header>
      <p className={styles.body}>{lm.long}</p>
      {parent && <p className={styles.note}>in {parent.name}</p>}
      <CrossLinks links={links} />
    </>
  );
}

function CrossLinks({ links }: { links: { slug: string; reason: string }[] }) {
  if (!links || links.length === 0) return null;
  return (
    <section className={styles.cross}>
      <h3 className={styles.sub}>practice this — try another app</h3>
      <ul className={styles.crossList}>
        {links.map((link) => (
          <li key={link.slug}>
            <a
              href={urlFor(link.slug)}
              className={styles.crossLink}
              target="_self"
              rel="noopener"
            >
              <span className={styles.crossName}>
                {APP_LABELS[link.slug] ?? link.slug}
                <span className={styles.crossArrow} aria-hidden="true">→</span>
              </span>
              <span className={styles.crossReason}>{link.reason}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
